// Search-matched homepage headline.
//
// Google Ads appends `kw={keyword}` to the homepage URL (campaign final URL
// suffix). We map that keyword to one of a few fixed themes and swap the hero
// headline so it echoes what the person searched for. The raw keyword is
// never shown on the page: only these pre-written, clinically neutral lines
// can appear, so nothing a visitor types into a URL reaches the page.
//
// Rules for the copy below: no pill brand names, no emergency contraception,
// no superlatives, no "up to 12 months". Crawlers and visitors without `kw`
// see the default headline, which is also what the prerendered HTML holds.

export type SearchTheme = "default" | "order" | "free" | "mini" | "repeat" | "start" | "switch" | "nogp";

export interface HeroCopy {
  line1: string;
  line2: string;
  sub: string;
}

export const HERO_COPY: Record<SearchTheme, HeroCopy> = {
  default: {
    line1: "Free contraceptive pill.",
    line2: "Delivered.",
    sub: "The smart way to get your contraception. Hassle free and clinically reviewed.",
  },
  order: {
    line1: "Order your pill online.",
    line2: "Free. Delivered.",
    sub: "Free on the NHS. A short call with a pharmacist, then your pill is posted to you.",
  },
  free: {
    line1: "Free NHS contraceptive pill.",
    line2: "Delivered.",
    sub: "The consultation, the pill and the delivery are all free on the NHS.",
  },
  mini: {
    line1: "Free mini pill online.",
    line2: "Delivered.",
    sub: "Start, repeat or switch your progestogen-only pill with a short pharmacist call.",
  },
  repeat: {
    line1: "Repeat your pill online.",
    line2: "Free. Delivered.",
    sub: "No GP appointment needed. A quick pharmacist check, then your next supply is posted.",
  },
  start: {
    line1: "Start the pill online.",
    line2: "Free. Delivered.",
    sub: "A pharmacist talks you through your options and checks the pill is right for you.",
  },
  switch: {
    line1: "Switch your pill online.",
    line2: "Free. Delivered.",
    sub: "Talk it through with a pharmacist and change pill without a GP appointment.",
  },
  nogp: {
    line1: "The pill, no GP appointment.",
    line2: "Free. Delivered.",
    sub: "A short call with a pharmacist instead of a GP visit. Free on the NHS.",
  },
};

// Order matters: the first match wins. More specific themes come first.
const RULES: Array<[RegExp, SearchTheme]> = [
  [/\b(mini ?pill|progest(o|e)gen|progesterone|pop)\b/, "mini"],
  [/\b(repeat|continue|refill|reorder|re-order)\b/, "repeat"],
  [/\b(switch|change)\b/, "switch"],
  [/\b(start|starting|first)\b/, "start"],
  [/\b(without (a )?(gp|prescription)|no gp|gp appointment)\b/, "nogp"],
  [/\b(free|nhs)\b/, "free"],
  [/\b(order|buy|online|delivery|deliver|get)\b/, "order"],
];

export function themeFromKeyword(raw: string | null | undefined): SearchTheme {
  if (!raw) return "default";
  let kw = raw;
  try {
    kw = decodeURIComponent(raw.replace(/\+/g, " "));
  } catch {
    // keep raw
  }
  kw = kw.toLowerCase().replace(/[^a-z0-9 -]/g, " ").replace(/\s+/g, " ").trim().slice(0, 100);
  if (!kw) return "default";
  for (const [re, theme] of RULES) {
    if (re.test(kw)) return theme;
  }
  return "default";
}

/** Reads `kw` from the current URL. Safe during prerender (no window). */
export function currentSearchTheme(): SearchTheme {
  if (typeof window === "undefined") return "default";
  try {
    return themeFromKeyword(new URLSearchParams(window.location.search).get("kw"));
  } catch {
    return "default";
  }
}
